/* eslint-disable no-param-reassign */

import axios from 'axios';
import i18n from '@/i18n';
import sortLanguages from '@/services/sortLanguages';
import { mergePhoneCodes, countryDisplayName } from '@/services/phoneCountries';
import {
  GET_PHONE_CODES, SET_PHONE_CODE, GET_DICTIONARY, GET_HR_DICTIONARY,
  GET_CITIES, GET_COUNTRIES, CITIES_FILTERS_CHANGE, GET_CITIES_CSV, GET_ACTIVE_INSURANCE_COMPANIES,
  GET_TIMEZONES, GET_CLIENT_PHONE_COUNTRIES,
} from '../actions/dictionary';

const setDefaultIcons = (list) => list.map((val) => {
  if (val.phoneRegion === 'CY') {
    return { ...val, flagIconWeb: 'https://350809.selcdn.ru/carefy-images/flag/web_cy.svg' };
  }
  return val;
});
const state = {
  ticketStatuses: [],
  ticketBusinessTypes: [],
  insuranceCompanies: [],
  activeInsuranceCompanies: [],
  offerStatuses: [],
  offerTypes: [],
  languages: [],
  userDoctorCallStatuses: [],
  userReceptionStatuses: [],
  ticketSourceTypeStatuses: [],
  clientCompanies: [],
  phoneCodes: {
    codes: [],
    selectedCountry: {
      code: '%2B33',
      flagIcon: '/img/renova-dashboard/icons/fra.svg',
      flagIconWeb: '/img/renova-dashboard/icons/fra.svg',
      id: '',
      name: 'France',
      phoneCode: '+33',
      phoneMobileMask: '#-##-##-##-##',
      phoneMasks: ['#-##-##-##-##'],
      phoneLength: 9,
      phoneLengthes: [9],
      phoneRegion: 'FR',
    },
  },
  loading: false,
  hrDictionary: [],
  hrLoading: false,
  cities: {
    items: [],
    filters: {
      page: 1,
      limit: 30,
      withPaging: true,
      countryIds: [],
      searchQuery: '',
    },
    totalCount: 1,
    loading: false,
  },
  countries: {
    items: [],
    filters: {
      withPaging: false,
    },
    loading: false,
  },
  timezones: [],
  clientPhoneCountries: {
    items: [],
    missingPhoneCount: 0,
    loading: false,
  },
};

const getters = {
  ticketStatuses: () => state.ticketStatuses,
  ticketBusinessTypes: () => state.ticketBusinessTypes,
  insuranceCompanies: () => state.insuranceCompanies,
  activeInsuranceCompanies: () => state.activeInsuranceCompanies,
  phoneCodes: () => state.phoneCodes,
  offerStatuses: () => state.offerStatuses,
  offerTypes: () => state.offerTypes,
  languages: () => state.languages,
  userDoctorCallStatuses: () => state.userDoctorCallStatuses,
  userReceptionStatuses: () => state.userReceptionStatuses,
  ticketSourceTypeStatuses: () => state.ticketSourceTypeStatuses,
  clientCompanies: () => state.clientCompanies,
  dictionaryLoading: () => state.loading,

  hrDictionary: () => state.hrDictionary,
  dictionaryHrLoading: () => state.hrLoading,

  cities: () => state.cities,
  countries: () => state.countries,
  timezones: () => state.timezones,
  clientPhoneCountries: () => state.clientPhoneCountries,

  /**
   * Filter insurance companies by selected client companies
   * @param state
   * @param_of_returned_function CompanyIds {Array} - array of client company ids to filter insurance companies
   * @returns {function(Array): *}
   */
  filteredInsuranceCompaniesByCompanyIds: (state) => (companyIds) => state.clientCompanies
    .reduce((acc, item) => {
      if (companyIds.includes(item.id)) {
        // To prevent duplicates will check if it is already in the array
        if (!acc.find((el) => el.id === item.insuranceCompany.id)) {
          acc.push(item.insuranceCompany);
        }
      }
      return acc;
    }, []),
};

const actions = {
  [GET_DICTIONARY]({ commit }, userId) {
    commit(GET_DICTIONARY, false);
    return axios.post('/insurance/dictionary/details', {}, { headers: { 'X-Locale': i18n.locale } })
      .then((resp) => {
        commit(GET_DICTIONARY, {
          data: resp.data,
          userId,
        });
        return Promise.resolve();
      })
      .catch((err) => {
        commit(GET_DICTIONARY, false);
        return Promise.reject(err.response.data);
      });
  },
  [GET_HR_DICTIONARY]({ commit }, domainId = '') {
    commit(GET_HR_DICTIONARY, false);

    return axios.post('/company/dictionary/details', {}, {
      headers: {
        'X-Locale': i18n.locale,
        'X-Domain-ID': domainId
      }
    })
      .then((resp) => {
        commit(GET_HR_DICTIONARY, resp.data);
        return Promise.resolve();
      })
      .catch((err) => {
        commit(GET_HR_DICTIONARY, false);
        return Promise.reject(err.response.data);
      });
  },
  [GET_PHONE_CODES]({ commit }) {
    return axios.get('/dicts/country-phone-code/list', { headers: { 'X-Locale': i18n.locale } })
      .then((resp) => {
        commit(GET_PHONE_CODES, setDefaultIcons(resp.data));
        return Promise.resolve();
      })
      .catch(() => {
        // Even when the dictionary request fails, keep the local country list -
        // otherwise phone fields are left without a single code to pick.
        commit(GET_PHONE_CODES, []);
        return Promise.reject();
      });
  },

  [GET_CITIES]({ commit, state }, payload) {
    commit(GET_CITIES);

    const filters = JSON.parse(JSON.stringify(state.cities.filters));
    filters.page -= 1;

    return axios.post(
      '/insurance/city/list',
      {
        ...filters,
        withPaging: payload && payload.withPaging ? payload.withPaging : false,
      },
      {
        headers: {
          'X-Locale': i18n.locale,
        },
      },
    )
      .then((resp) => {
        commit(GET_CITIES, resp.data);
        return Promise.resolve();
      })
      .catch((err) => {
        commit(GET_CITIES);
        return Promise.reject(err.response.data);
      });
  },
  [CITIES_FILTERS_CHANGE]({ commit, dispatch }, filters) {
    commit(CITIES_FILTERS_CHANGE, filters);
    dispatch(GET_CITIES);
  },
  [GET_CITIES_CSV]({ commit, state }) {
    const params = {
      withPaging: false,
      countryIds: state.cities.filters.countryIds,
    };

    return axios.post(
      '/insurance/city/list-in-csv',
      params,
      {
        responseType: 'arraybuffer',
      },
    )
      .then((resp) => resp.data)
      .catch((err) => {
        commit(GET_CITIES);
        return Promise.reject(err.response.data);
      });
  },

  [GET_COUNTRIES]({ commit, state }) {
    commit(GET_COUNTRIES);

    return axios.post(
      '/insurance/country/list',
      state.countries.filters,
      { headers: { 'X-Locale': i18n.locale } },
    )
      .then((resp) => {
        commit(GET_COUNTRIES, resp.data);
        return Promise.resolve();
      })
      .catch((err) => {
        commit(GET_COUNTRIES);
        return Promise.reject(err.response.data);
      });
  },
  [GET_ACTIVE_INSURANCE_COMPANIES]({ commit }) {
    return axios.post('/insurance/insurance-company/list', {
      statuses: ['ACTIVE']
    }, { headers: { 'X-Locale': i18n.locale } })
      .then((resp) => {
        commit(GET_ACTIVE_INSURANCE_COMPANIES, resp.data);
        return Promise.resolve();
      })
      .catch((err) => Promise.reject(err.response.data));
  },
  [GET_TIMEZONES]({ commit }) {
    return axios.get('/dicts/timezone')
      .then((resp) => {
        commit(GET_TIMEZONES, resp.data);
        return resp.data;
      })
      .catch((err) => Promise.reject(err.response?.data || err));
  },
  [GET_CLIENT_PHONE_COUNTRIES]({ commit }) {
    commit(GET_CLIENT_PHONE_COUNTRIES);

    return axios.post(
      '/insurance/client/phone-country/list',
      {},
      { headers: { 'X-Locale': i18n.locale } },
    )
      .then((resp) => {
        commit(GET_CLIENT_PHONE_COUNTRIES, resp.data);
        return Promise.resolve();
      })
      .catch((err) => {
        commit(GET_CLIENT_PHONE_COUNTRIES);
        return Promise.reject(err.response?.data || err);
      });
  },
};

const mutations = {
  [GET_DICTIONARY]: (state, resp) => {
    const { data, userId } = resp;
    if (!data) {
      state.loading = !state.loading;
    } else {
      state.ticketStatuses = data.ticketStatuses;
      state.ticketBusinessTypes = data.ticketBusinessTypes.filter((type) => type.id !== 'LEAD');
      state.insuranceCompanies = data.insuranceCompanies;
      state.offerStatuses = data.offerStatuses;
      state.offerTypes = data.offerTypes;
      state.userDoctorCallStatuses = data.userDoctorCallStatuses;
      state.userReceptionStatuses = data.userReceptionStatuses;
      state.ticketSourceTypeStatuses = data.ticketSourceTypeStatuses;
      state.clientCompanies = data.clientCompanies;
      state.languages = sortLanguages(data.languages);
      state.loading = false;
    }
  },
  [GET_HR_DICTIONARY]: (state, resp) => {
    if (!resp) {
      state.hrLoading = !state.hrLoading;
    } else {
      state.hrDictionary = resp;
      state.hrLoading = false;
    }
  },
  [GET_ACTIVE_INSURANCE_COMPANIES]: (state, resp) => {
    state.activeInsuranceCompanies = resp.items.map((val) => ({
      ...val,
      id: val.insuranceCompanyId
    }));
  },
  [GET_PHONE_CODES]: (state, resp) => {
    // Drop any FR the dictionary returns - the France entry spliced in below is the
    // authoritative one (correct mask + local flag), and two rows must never render.
    state.phoneCodes.codes = resp.filter((item) => item.phoneRegion !== 'FR');
    state.phoneCodes.codes.splice(1, 0, {
      id: '1',
      name: 'Argentina',
      phoneCode: '+54',
      phoneRegion: 'AR',
      isoCode2: 'AR',
      phoneMasks: ['[00]-[0000]-[0000]'],
      phoneMobileMask: '[00]-[0000]-[0000]',
      flagIcon: '/img/renova-dashboard/icons/arg.svg',
    });
    state.phoneCodes.codes.splice(2, 0, {
      id: '2',
      name: 'USA',
      phoneCode: '+1',
      phoneRegion: 'US',
      isoCode2: 'US',
      phoneMasks: ['[000]-[000]-[0000]'],
      phoneMobileMask: '[000]-[000]-[0000]',
      flagIcon: '/img/renova-dashboard/icons/usa.png',
    });
    state.phoneCodes.codes.splice(3, 0, {
      id: '3',
      name: 'Colombia',
      phoneCode: '+57',
      phoneRegion: 'CO',
      isoCode2: 'CO',
      phoneMasks: ['[000]-[000]-[0000]'],
      phoneMobileMask: '[000]-[000]-[0000]',
      flagIcon: '/img/renova-dashboard/icons/col.svg',
    });
    state.phoneCodes.codes.splice(4, 0, {
      id: '4',
      name: 'Switzerland',
      phoneCode: '+41',
      phoneRegion: 'CH',
      isoCode2: 'CH',
      phoneMasks: ['[00]-[000]-[00]-[00]'],
      phoneMobileMask: '[00]-[000]-[00]-[00]',
      flagIcon: '/img/renova-dashboard/icons/che.svg',
      flagIconWeb: '/img/renova-dashboard/icons/che.svg',
    });
    state.phoneCodes.codes.splice(5, 0, {
      id: '5',
      name: 'France',
      phoneCode: '+33',
      phoneRegion: 'FR',
      isoCode2: 'FR',
      phoneMasks: ['[0]-[00]-[00]-[00]-[00]'],
      phoneMobileMask: '[0]-[00]-[00]-[00]-[00]',
      flagIcon: '/img/renova-dashboard/icons/fra.svg',
      flagIconWeb: '/img/renova-dashboard/icons/fra.svg',
    });
    // Countries from the backend dictionary (and the hardcoded ones above) come
    // first, the rest is appended from the local list in alphabetical order.
    state.phoneCodes.codes = mergePhoneCodes(state.phoneCodes.codes);
    state.phoneCodes.codes.forEach((item) => {
      item.code = item.phoneCode.replace('+', '%2B');
      const re1 = /[[\]]/g;
      const re2 = /0/g;
      const re3 = /[^+#]/g;
      item.phoneMobileMask = item.phoneMobileMask.replace(re1, '').replace(re2, '#');
      item.phoneLength = item.phoneMobileMask.replace(re3, '').length;
      item.phoneMasks = item.phoneMasks.map((mask) => mask.replace(re1, '').replace(re2, '#'));
      item.phoneLengthes = item.phoneMasks.map((mask) => mask.replace(re1, '').replace(re2, '#').replace(re3, '').length);
      if (item.phoneRegion === 'CY') {
        item.flagIcon = '/img/renova-dashboard/icons/cyprus.svg';
      }
      item.displayName = countryDisplayName(item);
    });
  },
  [SET_PHONE_CODE]: (state, phoneCode) => {
    state.phoneCodes.selectedCountry = phoneCode;
  },

  [GET_CITIES]: (state, resp) => {
    if (!resp) {
      state.cities.loading = !state.cities.loading;
    } else {
      state.cities.items = resp.items;
      state.cities.totalCount = resp.totalCount;
      state.cities.loading = false;
    }
  },
  [CITIES_FILTERS_CHANGE]: (state, filters) => {
    state.cities.filters = filters;
  },

  [GET_COUNTRIES]: (state, resp) => {
    if (!resp) {
      state.countries.loading = !state.countries.loading;
    } else {
      state.countries.items = resp.items;
      state.countries.loading = false;
    }
  },
  [GET_TIMEZONES]: (state, resp) => {
    state.timezones = resp;
  },
  [GET_CLIENT_PHONE_COUNTRIES]: (state, resp) => {
    if (!resp) {
      state.clientPhoneCountries.loading = true;
    } else {
      state.clientPhoneCountries.items = resp.items || [];
      state.clientPhoneCountries.missingPhoneCount = resp.missingPhoneCount || 0;
      state.clientPhoneCountries.loading = false;
    }
  },
};
export default {
  state,
  getters,
  actions,
  mutations,
};
